/**
 * What a theme choice does to <html> (spec docs/superpowers/specs/2026-09-27-app-ready-made-themes-design.md,
 * "Ruling 2" and "Ruling 6"). The client half of the first-paint script (app-theme-boot.ts), which
 * follows the same rules — a spec runs both over the same cases.
 *
 *   - `data-app-theme` on <html>, not on the app's wrapper: dialogs, menus, popovers and toasts
 *     render in portals at the end of <body>, outside the app's frame. Every choice is written —
 *     Classic switches the builder off, Custom lets it through — and someone who never chose gets
 *     nothing at all.
 *   - the Inter variable class on <html> with it: it only defines --font-app-inter, which Ledger's
 *     font-family names (and the menu's, while Ledger is saved behind a preview).
 *   - `data-app-previewing` while the menu previews a choice, naming the look it stands in front of.
 *   - light and dark stay next-themes' (Mode applies to every choice), so nothing here touches the
 *     `light` / `dark` class.
 */
import { APP_PREVIEWING_ATTRIBUTE, APP_THEME_ATTRIBUTE, isAppThemeChoice, type AppThemeChoice } from './app-themes'

/**
 * Where a ready-made theme never applies, whatever the cookie says: the play area, the learner
 * portal and its previews, the SCORM player and the admin console. Only an app page (one under the
 * app sidebar) ever mounts the theme scope; this list is the belt to that pair of braces.
 */
export const APP_THEME_OFF_PATHS = [
  '/play',
  '/phishtrainer/play',
  '/portal',
  '/portal-preview',
  '/portal-theme-preview',
  '/user-portal',
  '/teams-portal',
  '/player',
  '/admin',
] as const

/** Exact-or-slash, so `/play` and `/play/me` are off, `/playbooks` is not. */
export function isAppThemeOffPath(pathname: string): boolean {
  return APP_THEME_OFF_PATHS.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
}

/** A next/font class name is plain: letters, digits, `_` and `-`. Anything else never reaches <html>. */
export const isPlainClassName = (value: unknown): value is string =>
  typeof value === 'string' && /^[A-Za-z0-9_-]+$/.test(value)

/** Put a choice (and the Inter class) on <html>. Anything outside the catalogue takes them off. */
export function applyAppTheme(theme: AppThemeChoice, fontClass?: string, doc: Document = document): void {
  if (!isAppThemeChoice(theme)) {
    clearAppTheme(fontClass, doc)
    return
  }
  const root = doc.documentElement
  root.setAttribute(APP_THEME_ATTRIBUTE, theme)
  if (isPlainClassName(fontClass)) root.classList.add(fontClass)
}

/** Mark a preview on screen (`saved` is the look it stands in front of), or clear the mark. */
export function markAppThemePreview(saved: AppThemeChoice | null, doc: Document = document): void {
  const root = doc.documentElement
  if (saved !== null && isAppThemeChoice(saved)) root.setAttribute(APP_PREVIEWING_ATTRIBUTE, saved)
  else root.removeAttribute(APP_PREVIEWING_ATTRIBUTE)
}

/** Take the choice, the preview mark and the Inter class off <html>, leaving everything else as it is. */
export function clearAppTheme(fontClass?: string, doc: Document = document): void {
  const root = doc.documentElement
  root.removeAttribute(APP_THEME_ATTRIBUTE)
  root.removeAttribute(APP_PREVIEWING_ATTRIBUTE)
  if (isPlainClassName(fontClass)) root.classList.remove(fontClass)
}
